import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import { Switch } from "../../../components/ui/switch";
import { Label } from "../../../components/ui/label";
import { Alert } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Stack } from "../../../components/ui/stack";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import syntaxTheme from "../../../shared-components/syntaxTheme";
import syntaxHighlighterStyle from "../../../shared-components/syntaxHighlighterStyle";

import SearchIcon from "@mui/icons-material/Search";
import { LoaderCircle } from "lucide-react";
import { DataGridPro, GridColDef, useGridApiRef } from "@mui/x-data-grid-pro";
import { TimeRange } from "../types";
import { useClusterInfraEvents, useAzureLogs } from "../../../crud/logs/hooks";
import { formatInTimeZone } from "date-fns-tz";
import {
  BooleanParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";
import { useDebounce, useTimezone } from "../../../utils/hooks";
import { Download, MoreVert } from "@mui/icons-material";
import { timeRangeToString } from "./utils";
import { LogEntry } from "../../../crud/logs/types";
import { LogsHistogram } from "./charts/LogsHistogram";
import { ClusterFrontendSchema } from "../../../api-client";
import { REFRESH_LOGS_INTERVAL } from "./const";
import { getStateColorClass } from "./badge-colors";

export const AzureLogsView = ({
  clusterId,
  cluster,
  timeRange,
  setTimeRange,
  defaultLogTimeRange,
  defaultMetricsTimeRange,
  refAreaLeft,
  refAreaRight,
  setRefAreaLeft,
  setRefAreaRight,
  clusterIsRunning,
  zoomOut,
}: {
  clusterId: string;
  cluster?: ClusterFrontendSchema;
  timeRange?: TimeRange;
  setTimeRange: (timeRange: TimeRange | undefined) => void;
  defaultLogTimeRange?: TimeRange;
  defaultMetricsTimeRange?: TimeRange;
  refAreaLeft: number;
  refAreaRight: number;
  setRefAreaLeft: (refAreaLeft: number) => void;
  setRefAreaRight: (refAreaRight: number) => void;
  clusterIsRunning?: boolean;
  zoomOut: () => void;
}): React.ReactElement => {
  const liveUpdates = timeRange === defaultMetricsTimeRange && clusterIsRunning;
  const apiRef = useGridApiRef();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [highlightInstance, setHighlightInstance] = useState(-1);
  const [logHistoryComplete, setLogHistoryComplete] = useState(false);
  const [earliestTimestamp] = useState<number>();

  const [displayTz] = useTimezone();

  const showMenu = Boolean(menuAnchorEl);
  const [dask, setDask] = useQueryParam(
    "daskLogs",
    withDefault(BooleanParam, true),
  );
  const [system, setSystem] = useQueryParam(
    "systemLogs",
    withDefault(BooleanParam, false),
  );

  const [showInfraEvents, setShowInfraEvents] = useQueryParam(
    "showLifecycle",
    withDefault(BooleanParam, true),
  );

  const [filterTimeRange, setFilterTimeRange] = useQueryParam(
    "filterLogTime",
    withDefault(BooleanParam, true),
  );

  const [filterPattern, setFilterPattern] = useQueryParam(
    "filterPattern",
    withDefault(StringParam, ""),
  );

  const instanceLabelMap: Map<number, string> = useMemo(() => {
    const labelMap = new Map(
      cluster?.workersExceptUnprovisionable.map((workerProcess) => {
        return [
          workerProcess?.instance?.id || 0,
          workerProcess?.instance?.privateIpAddress || "",
        ];
      }),
    );
    if (cluster?.scheduler?.instance?.id) {
      labelMap.set(cluster.scheduler.instance.id, "scheduler");
    }
    return labelMap;
  }, [cluster]);

  const getInstanceLabel = useCallback(
    (instanceId?: number) => {
      return instanceId
        ? instanceLabelMap.get(instanceId) || String(instanceId)
        : "";
    },
    [instanceLabelMap],
  );

  // should this be memo-ized? is this causing rerenders?
  const renderInstanceLabel = useCallback(
    (instanceId: number) => {
      const label = getInstanceLabel(instanceId);
      return (
        <span
          onMouseEnter={() => {
            setHighlightInstance(instanceId);
          }}
          onMouseLeave={() => {
            setHighlightInstance(-1);
          }}
          onClick={() => {
            setFilterPattern(`instance:${label} ${filterPattern} `.trim());
          }}
          className="font-mono text-sm cursor-pointer"
        >
          {instanceId === highlightInstance ? (
            <b>
              <u>{label}</u>
            </b>
          ) : (
            label
          )}
        </span>
      );
    },
    [
      getInstanceLabel,
      setFilterPattern,
      highlightInstance,
      setHighlightInstance,
      filterPattern,
    ],
  );

  // DataGridPro can't correctly set the width of a column that goes outside the
  // viewport, so we'll calculate the  width of the longest message and set it as the
  // width of the column
  const messageEls = document.querySelectorAll('div[data-field="message"]>*');
  const maxMessageWith = Math.max(
    ...Array.from(messageEls).map((item) => item.clientWidth),
  );

  const Columns: GridColDef<LogEntry>[] = useMemo(
    () => [
      {
        field: "timestamp",
        headerName: `Time (${displayTz})`,
        type: "dateTime",
        valueFormatter: ({ value }) =>
          formatInTimeZone(value, displayTz, "yyyy-MM-dd HH:mm:ss.SSSS"),
        minWidth: 220,
      },
      {
        field: "instanceId",
        headerName: "Instance",
        minWidth: 120,
        // "scheduler" or private IP address for workers
        renderCell: ({ value }) => renderInstanceLabel(value),
        valueFormatter: ({ value }) => getInstanceLabel(value),
      },
      {
        field: "message",
        headerName: "Message",
        width: maxMessageWith,
        minWidth: 220,
        renderCell: (params) => {
          const message = params.row.message;
          return (
            <>
              {params.row.state && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge
                        key={`chip${params.row.id}_${params.row.instanceId}`}
                        className={getStateColorClass(
                          params.row.stateColor,
                          params.row.stateFilled,
                        )}
                      >
                        {params.row.state}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{params.row.stateExplanation}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {params.row.logType === "dask" ? (
                <SyntaxHighlighter
                  language="python"
                  style={syntaxTheme}
                  customStyle={syntaxHighlighterStyle}
                >
                  {message}
                </SyntaxHighlighter>
              ) : (
                <div className="whitespace-nowrap">{message}</div>
              )}
            </>
          );
        },
      },
    ],
    [displayTz, maxMessageWith, renderInstanceLabel, getInstanceLabel],
  );

  const query = useAzureLogs(Number(clusterId), dask, system, liveUpdates);

  // much faster and less error prone than cluster logs, so for now ignoring fetch and error states
  const { data: infraEventData } = useClusterInfraEvents(
    clusterId,
    defaultLogTimeRange,
  );

  useEffect(() => {
    if (query.isFetched && !query.hasPreviousPage) {
      setLogHistoryComplete(true);
    }
  }, [query.isFetched, query.hasPreviousPage]);

  const logs = useMemo(() => {
    // for azure
    const flatLogs = (query.data?.pages.flatMap((page) =>
      (page.lines || []).map((line, index) => ({
        id: page.id * REFRESH_LOGS_INTERVAL + index,
        ...line,
      })),
    ) || []) as LogEntry[];

    const flatFullLogs = flatLogs.concat(
      (showInfraEvents && infraEventData) || [],
    );

    const filteredLogs = filterLogs(
      flatFullLogs,
      instanceLabelMap,
      filterPattern,
      filterTimeRange,
      timeRange,
    );

    const dedupedLogs = dedupeLogs(filteredLogs);

    return dedupedLogs;
  }, [
    query.data?.pages,
    infraEventData,
    showInfraEvents,
    filterPattern,
    timeRange,
    filterTimeRange,
    instanceLabelMap,
  ]);

  const hasZoomed = timeRange !== defaultMetricsTimeRange;
  const showFilterAlert = filterPattern && !logHistoryComplete;

  const filterAlertRef = useRef<HTMLDivElement>(null);
  const filterAlertHeight = filterAlertRef.current
    ? filterAlertRef.current.clientHeight + 8 // 8px for spacing
    : 0;

  const zoomMessageRef = useRef<HTMLDivElement>(null);
  const zoomMessageHeight = zoomMessageRef.current
    ? zoomMessageRef.current.clientHeight + 8 // 8px for spacing
    : 0;

  // DataGridPro needs us to manually set the height of the grid container
  // so that virtualization works properly
  const topHeight = 220 + filterAlertHeight + zoomMessageHeight;

  const handleClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  return (
    <div className="h-full pb-0">
      <Stack spacing={1} className="h-full relative">
        <div className="flex flex-row items-center gap-4 mb-1">
          <FilterField
            filterPattern={filterPattern}
            setFilterPattern={setFilterPattern}
          />

          <div className="flex items-center space-x-2">
            <Switch
              id="dask-switch"
              checked={dask}
              onCheckedChange={() => setDask(!dask)}
            />
            <Label htmlFor="dask-switch">Application</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="system-switch"
              checked={system}
              onCheckedChange={() => setSystem(!system)}
            />
            <Label htmlFor="system-switch">System</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="lifecycle-switch"
              checked={showInfraEvents}
              onCheckedChange={() => setShowInfraEvents(!showInfraEvents)}
            />
            <Label htmlFor="lifecycle-switch">Lifecycle</Label>
          </div>
          <div>
            <Button onClick={handleClickMenu} variant="ghost" size="icon">
              <MoreVert className="h-4 w-4" />
            </Button>
            <Menu
              anchorEl={menuAnchorEl}
              open={showMenu}
              onClose={handleCloseMenu}
            >
              <MenuItem>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="filter-time-switch"
                    checked={filterTimeRange}
                    onCheckedChange={() => setFilterTimeRange(!filterTimeRange)}
                  />
                  <Label htmlFor="filter-time-switch">
                    Filter by selected time
                  </Label>
                </div>
              </MenuItem>
            </Menu>
          </div>
        </div>
        {query.error ? (
          <Alert variant="destructive">{query.error?.message}</Alert>
        ) : null}
        {showFilterAlert ? (
          <Alert variant="info" ref={filterAlertRef}>
            Filtered results can be sparse while logs are loading.
          </Alert>
        ) : null}
        <LogsHistogram
          logs={logs}
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          defaultTimeRange={
            // Charts only show the "No Data" message if the user hasn't zoomed (timeRange != defaultTimeRange), let's
            // also make that check fail when a filter pattern has been entered to avoid showing the "No Data" message
            filterPattern ? undefined : defaultMetricsTimeRange
          }
          refAreaLeft={refAreaLeft}
          refAreaRight={refAreaRight}
          setRefAreaLeft={setRefAreaLeft}
          setRefAreaRight={setRefAreaRight}
        />
        {timeRange && hasZoomed && filterTimeRange && (
          <div className="pl-1" ref={zoomMessageRef}>
            <span>Showing logs between</span>{" "}
            <Badge
              variant="secondary"
              className="cursor-pointer"
              onClick={zoomOut}
            >
              {timeRangeToString(timeRange)}
            </Badge>
          </div>
        )}
        <div
          className="relative border-b border-gray-300"
          style={{ height: `calc(100vh - ${topHeight}px)` }}
        >
          <div className="absolute top-0 right-0 pt-0 z-10">
            <div
              className={`inline-block align-text-top transition-opacity ${
                query.isFetching || !logHistoryComplete
                  ? "opacity-100"
                  : "opacity-0"
              }`}
            >
              <LoaderCircle className="animate-spin w-4 h-4" />
            </div>
            {!logHistoryComplete && earliestTimestamp ? (
              <div className="inline-block ml-1">
                <span style={{ fontStyle: "italic" }}>
                  Loading logs prior to{" "}
                </span>
                <Badge variant="secondary">
                  {formatInTimeZone(
                    new Date(earliestTimestamp),
                    displayTz,
                    "LLL do, HH:mm:ss",
                  )}
                </Badge>
              </div>
            ) : null}
            {timeRange && cluster ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      onClick={() =>
                        apiRef.current.exportDataAsCsv({
                          fileName: `${new Date(
                            timeRange[0],
                          ).toISOString()}_to_${new Date(
                            timeRange[1],
                          ).toISOString()}_${cluster.name}_logs.csv`,
                        })
                      }
                      variant="ghost"
                      size="icon"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Download log range as CSV</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
          </div>
          <DataGridPro
            apiRef={apiRef}
            getRowId={(row) => row.id ?? logs.indexOf(row)}
            density="compact"
            getRowHeight={() => "auto"}
            hideFooter
            disableRowSelectionOnClick
            sx={() => ({
              "& .MuiDataGrid-cell": {
                border: 0,
                fontFamily: "Roboto Mono, monospace",
                fontSize: "13px",
                alignItems: "flex-start",
              },
              "& .MuiDataGrid-overlay": query.isFetching
                ? {
                    backgroundColor: "transparent",
                  }
                : {},
            })}
            rows={logs || []}
            columns={Columns}
            initialState={{
              sorting: {
                sortModel: [{ field: "timestamp", sort: "desc" }],
              },
            }}
          />
        </div>
      </Stack>
    </div>
  );
};

const FilterField = ({
  filterPattern,
  setFilterPattern,
}: {
  filterPattern: string | undefined;
  setFilterPattern: (filterPattern: string | undefined) => void;
}) => {
  const [localFilterPattern, setLocalFilterPattern] = useState(filterPattern);
  const debouncedFilterPattern = useDebounce(localFilterPattern, 400);

  useEffect(() => {
    setFilterPattern(debouncedFilterPattern);
  }, [setFilterPattern, debouncedFilterPattern]);

  useEffect(() => {
    // Sync external filterPattern prop to local state
    // localFilterPattern is intentionally omitted from dependencies
    // to prevent infinite loop
    if (filterPattern !== localFilterPattern) {
      setLocalFilterPattern(filterPattern);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPattern]);

  return (
    <div className="relative flex-1 mb-1 pr-4">
      <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
      <Input
        placeholder="Filter events"
        value={localFilterPattern}
        className="pl-10"
        onChange={(e) => {
          setLocalFilterPattern(e.target.value || "");
        }}
      />
    </div>
  );
};

const inRange = (timestamp: number, timeRange?: TimeRange) => {
  if (!timeRange) {
    return true;
  }
  return timestamp >= timeRange[0] && timestamp <= timeRange[1];
};

// from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
const escapeRegExp = (s: string) => {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
};

const dedupeLogs = (logs: LogEntry[]) => {
  const seen = new Set<string>();
  return logs.filter((log) => {
    const key = `${log.timestamp}${log.instanceId}${log.message}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

// Filter logs by filter pattern and time range
// TODO: Make this less loopy
const filterLogs = (
  logs: LogEntry[],
  instanceLabelMap: Map<number, string>,
  filterPattern: string,
  filterTimeRange: boolean,
  timeRange?: TimeRange,
) => {
  const logsWithId = logs.map((log, id) => ({ id, ...log }));
  if (!filterPattern) {
    return logsWithId.filter(
      (log) => !filterTimeRange || inRange(log.timestamp, timeRange),
    );
  }

  // if the filter string is "instance:123 foo", then match "123" on the instance and "foo" on the message
  const filters = filterPattern.split(" ");
  const instanceFilters = filters
    .filter((value) => value.startsWith("instance:"))
    .map((value) => value.replace("instance:", ""));
  const nonInstancePattern = filters
    .filter((value) => !value.startsWith("instance:"))
    .join(" ");
  const literalRegex = new RegExp(escapeRegExp(nonInstancePattern), "i");

  let regexRegex: RegExp | undefined;
  try {
    regexRegex = new RegExp(nonInstancePattern, "i");
  } catch {
    // the filter string isn't a valid regex, for example "[foo"
  }

  const filteredLogs = logsWithId.filter(
    (log) =>
      // match on message
      (literalRegex.test(log.message) ||
        (regexRegex && regexRegex.test(log.message)) ||
        // or on instance IP address
        (nonInstancePattern.includes(".") &&
          log.instanceId &&
          instanceLabelMap.get(log.instanceId) &&
          literalRegex.test(instanceLabelMap.get(log.instanceId) || "")) ||
        // or on state
        (log.state &&
          (literalRegex.test(log.state) ||
            (regexRegex && regexRegex.test(log.state))))) &&
      // and on instance if there's an instance filter
      (!instanceFilters.length ||
        (log.instanceId &&
          instanceLabelMap.get(log.instanceId) &&
          instanceFilters.includes(
            instanceLabelMap.get(log.instanceId) || "",
          ))) &&
      // and in time range if range is specified
      (!filterTimeRange || inRange(log.timestamp, timeRange)),
  );
  return filteredLogs;
};
